import {
  VideoUploadAction,
  VideoUploadItem,
  VideoUploadPhase,
} from './video-upload.types';

export const MAX_CONCURRENT_UPLOADS = 3;

const ACTIVE_PHASES: VideoUploadPhase[] = ['uploading', 'completing'];

const update = (
  items: VideoUploadItem[],
  videoId: string,
  changes: Partial<VideoUploadItem>
): VideoUploadItem[] =>
  items.map((item) =>
    item.videoId === videoId ? { ...item, ...changes } : item
  );

export const videoUploadQueueReducer = (
  items: VideoUploadItem[],
  action: VideoUploadAction
): VideoUploadItem[] => {
  switch (action.type) {
    case 'enqueued':
      return [
        ...items,
        ...action.uploads.map(
          ({ video, upload_url, file, contentType }): VideoUploadItem => ({
            videoId: video.id,
            title: video.title,
            video,
            file,
            uploadUrl: upload_url,
            contentType,
            phase: 'queued',
            uploadedBytes: 0,
            error: null,
            resume: false,
          })
        ),
      ];

    case 'started':
      return items.map((item) =>
        item.videoId === action.videoId && item.phase === 'queued'
          ? { ...item, phase: 'uploading' }
          : item
      );

    case 'progressed':
      return update(items, action.videoId, {
        uploadedBytes: action.uploadedBytes,
      });

    case 'completing':
      return items.map((item) =>
        item.videoId === action.videoId
          ? { ...item, phase: 'completing', uploadedBytes: item.file.size }
          : item
      );

    case 'finished':
      return items.map((item) =>
        item.videoId === action.video.id
          ? {
              ...item,
              phase: 'finished',
              video: action.video,
              uploadedBytes: item.file.size,
            }
          : item
      );

    case 'failed':
      return update(items, action.videoId, {
        phase: 'failed',
        error: action.error,
      });

    case 'cancelled':
      return items.map((item) =>
        item.videoId === action.videoId && isUploadPending(item)
          ? { ...item, phase: 'failed', error: '' }
          : item
      );

    case 'retried':
      return update(items, action.videoId, {
        phase: 'queued',
        error: null,
        resume: true,
      });

    case 'removed':
      return items.filter((item) => item.videoId !== action.videoId);

    case 'finishedCleared':
      return items.filter((item) => item.phase !== 'finished');

    default: {
      const unhandledAction: never = action;

      return unhandledAction;
    }
  }
};

export const isUploadActive = (item: VideoUploadItem): boolean =>
  ACTIVE_PHASES.includes(item.phase);

export const uploadsToStart = (
  items: VideoUploadItem[],
  maxConcurrent = MAX_CONCURRENT_UPLOADS
): VideoUploadItem[] =>
  items
    .filter((item) => item.phase === 'queued')
    .slice(0, Math.max(0, maxConcurrent - items.filter(isUploadActive).length));

export const uploadPercent = ({
  uploadedBytes,
  file,
}: VideoUploadItem): number =>
  file.size ? Math.round((uploadedBytes / file.size) * 100) : 0;

export const isUploadPending = (item: VideoUploadItem): boolean =>
  item.phase === 'queued' || isUploadActive(item);

export const getUploadTotals = (items: VideoUploadItem[]) => ({
  totalBytes: items.reduce((sum, item) => sum + item.file.size, 0),
  uploadedBytes: items.reduce((sum, item) => sum + item.uploadedBytes, 0),
  pending: items.filter(isUploadPending).length,
  failed: items.filter((item) => item.phase === 'failed').length,
  finished: items.filter((item) => item.phase === 'finished').length,
});
